import Fmt from './Fmt';
import * as _ from 'lodash';

/*
 A scoring system of 0 to 5 is used in several places.
 0 means lack of significant data/change - it is neutral, not really bad.
 1 is bad
 5 is good
*/
class Scorer {

    addScoreData(data) {
        this._data = data;
        _.each(this._data, c => this._scoreClient(c));
    }

    _scoreClient(d) {
        // all visits
        this._addScoreToTimeSeries(d.visits.all.weekly, 500, 15);

        // app sessions
        this._addScoreToTimeSeries(d.app_sessions.weekly, 0, 35);

        // tags
        this._addScoreToTimeSeries(d.tagged_items.weekly, 0, 35);

        // product catalog
        let catalogScore = 1;
        let catalogMessage = "No catalogs connected";
        if (d.product_catalogs.catalogs.length > 0) {
            _.each(d.product_catalogs.catalogs, this._addScoreToCatalog);

            catalogScore = _.chain(d.product_catalogs.catalogs).map(x => x.score).min().value();
            catalogMessage = _.chain(d.product_catalogs.catalogs).map(x => x.score_message).value().join(" ");

            if (catalogScore < 5) {
                catalogMessage += " Note: The refresh time shows the last time the catalog actually changed. The time " +
                    " displayed on the catalog health page in the app shows the last time the catalog was successfully retrieved.";
            }
        }

        d.product_catalogs.score = catalogScore;
        d.product_catalogs.score_message = catalogMessage;

        // revenue connections
        let revenueScore = 1;
        let revenueMessage = "No revenue sources connected";
        let revenueIsValid = false;

        if (d.revenue_connections.connections.length > 0) {
            const c = _.chain(d.revenue_connections.connections).sortBy(x => x.last_success_epoch).reverse().head().value();
            if (c.is_valid) {
                revenueIsValid = true;
                revenueScore = 5;
                revenueMessage = "";
            } else {
                revenueScore = 2;
                revenueMessage = "A connection exists but is no longer valid. "
            }

            revenueMessage += `Connection type: ${c.service}. Last success: ${Fmt.dt(c.last_success_epoch)}`;
        }

        d.revenue_connections.has_valid_connection = revenueIsValid;
        d.revenue_connections.score = revenueScore;
        d.revenue_connections.score_message = revenueMessage;

        // active users
        let activeUserScore = 1;

        if (d.active_users.count === 1) {
            activeUserScore = 3;
        } else if (d.active_users.count >= 2) {
            activeUserScore = 5;
        }

        d.active_users.score = activeUserScore;
        d.active_users.score_message = `${d.active_users.count} active user${d.active_users.count === 1 ? "" : "s"}. ` +
            "An active user is defined as a user with at least 4 total sessions over the last 4 weeks, and the goal is a minimum of 2 active users.";


        // fanreel onsite
        const frMin = 100;
        const frMinGood = 500;
        const frWeek = d.visits.fanreel.weekly.current;
        let fanreelScore = 1;
        let fanreelMessage = "Very low Fanreel visits last week: " + Fmt.num(frWeek);

        // base the score off the last week for now since that will update faster when a reel is implemented
        if (frWeek > frMinGood) {
            fanreelScore = 5;
            fanreelMessage = "Visits last week: " + Fmt.num(frWeek);
        } else if (frWeek > frMin) {
            fanreelScore = 3;
            fanreelMessage = "Low visits last week: " + Fmt.num(frWeek);
        }

        if (fanreelScore < 4 && d.visits.likely_api_consumer) {
            // If the client is using the Fanreel API, give them full credit for an on-site gallery.
            fanreelScore = 5;
            fanreelMessage = `This client appears to be consuming the API and made ${Fmt.num(d.visits.api_impressions.weekly.current)} calls to the API last week.`
        }

        d.visits.fanreel.score = fanreelScore;
        d.visits.fanreel.score_message = fanreelMessage;

        this._addClientHealthScore(d);
    }

    _addClientHealthScore(c) {

        /*
        The score consists of:
        SETUP POINTS (Max 30)
        Revenue Connected:  Max of 5 points
        Active Users:  Max of 5 points
        Catalog Connected:  Max of 10 points
        Fanreel OnSite:  Max of 10 points

        For the score for the setup items, we take the score of each relevant component (a score of 0-5 assigned above)
        and use:
        0 -> 0% (0 means lack of information, not a really low score)
        1 -> 0%
        2 -> 25%
        3 -> 50%
        4 -> 75%
        5 - 100%

        USAGE POINTS (Max 70)
        Visits Points (Max 15)
        Min(15, total visits current 4 weeks / 80000 * 15)

        Sessions Points (Max 55)
        Min(total sessions current 4 weeks, 55)

        */

        const score = {};
        score.components = [];

        score.components.push(this._getClientHealthScoreComponent("Active Users", 5, this._calculateSetupScore(c.active_users.score, 5)));
        score.components.push(this._getClientHealthScoreComponent("Catalog", 10, this._calculateSetupScore(c.product_catalogs.score, 10)));
        score.components.push(this._getClientHealthScoreComponent("Fanreel Onsite", 10, this._calculateSetupScore(c.visits.fanreel.score, 10)));
        score.components.push(this._getClientHealthScoreComponent("Rev. Conn.", 5, this._calculateSetupScore(c.revenue_connections.score, 5)));

        let visitsPlusApiImpressions = c.visits.all.weekly.four_week_current;
        if (c.visits.api_impressions) {
          visitsPlusApiImpressions += c.visits.api_impressions.weekly.four_week_current;
        }
        const visitsScore = Math.min(15, visitsPlusApiImpressions / 80000 * 15);
        score.components.push(this._getClientHealthScoreComponent("Visits", 15, visitsScore));

        const sessionsScore = Math.min(55, c.app_sessions.weekly.four_week_current);
        score.components.push(this._getClientHealthScoreComponent("Sessions", 55, sessionsScore));

        score.score = Math.round(_.sumBy(score.components, x => x.score));
        score.meta_score = Math.max(1, Math.floor(score.score/20));

        c.health_score = score;
    }

    _calculateSetupScore(score0to5, max) {
        const multiplier = (score0to5 === 0) ? 0 : (score0to5 - 1) * 0.25;
        return multiplier * max;
    }

    _getClientHealthScoreComponent(name, max, score) {
        const s = {};
        s.name = name;
        s.max = max;
        s.score = score;
        return s;
    }


    _addScoreToCatalog(c) {
        let score = 5;
        let score_message = ""

        if (c.days_stale > 7) {
            score = 2;
            score_message += c.feed_name + ": Has not been refreshed since " + Fmt.dt(c.version_timestamp) + ". ";
        }

        if (c.greatest_percent_failed >= 20) {
            score = 2;
            score_message += c.feed_name + ": Ingestion errors for up to " + Fmt.num(c.greatest_percent_failed) + "% of items";
        }

        if (score === 5) {
            score_message = `Catalog '${c.feed_name}' is healthy. `;
        }

        c.score = score;
        c.score_message = score_message;
    }

    // scores a time series from 1 (bad) to 5 (good)
    _addScoreToTimeSeries(ts, minValueForSignificance, minPercentageChange) {
        let score = 0;
        if (ts.current >= minValueForSignificance && Math.abs(ts.percentage_delta) >= minPercentageChange) {
            score = ts.percentage_delta > 0 ? 5 : 1;
        }

        let fourWeekScore = 3;
        if (ts.four_week_current >= 4 * minValueForSignificance && Math.abs(ts.four_week_percentage_delta) >= minPercentageChange) {
            fourWeekScore = ts.four_week_percentage_delta > 0 ? 5 : 1;
        }

        ts.score = score;
        ts.four_week_score = fourWeekScore;
    }
}

export default Scorer;



// WEBPACK FOOTER //
// ./src/Scorer.js